import React, { useEffect, useRef, useState, useCallback } from "react";
import { API } from "@/lib/api";
import client from "@/lib/api";
import { MessageSquare, Send, WifiOff } from "lucide-react";
import { VoiceRecorder } from "@/components/VoiceRecorder";
import { VoiceBubble } from "@/components/VoiceBubble";

/** Technician-side chat panel.
 *
 *  Reliability contract (V1):
 *    - WS stays CONNECTED throughout LIVE and PAUSED (canConnect); only the
 *      send input is gated on LIVE (canSend).
 *    - `expanded` (drawer open/close) is NOT in the WS effect deps — opening
 *      the drawer must never close/reopen the socket.
 *    - 20 s application-layer ping to survive proxy idle timeouts.
 *    - loadHistory() runs on every successful WS reconnect so any messages
 *      that arrived during a disconnect gap re-appear without a page refresh.
 *    - Messages deduped by id — history reload + live broadcast can overlap
 *      safely.
 */
export function ChatPanelTechnician({ stationId, order, onSystemMessage }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [connected, setConnected] = useState(false);
  const [meta, setMeta] = useState(null);
  const [unread, setUnread] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const wsRef = useRef(null);
  const backoffRef = useRef(1000);
  const scrollRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const expandedRef = useRef(expanded);
  useEffect(() => { expandedRef.current = expanded; }, [expanded]);

  // Chat is fully usable (send + receive) during LIVE, PAUSED, and TESTING.
  // Keeping the WS open during TESTING is REQUIRED for realtime testing_*
  // broadcasts to reach the technician. Only the SEND input is gated on
  // LIVE|PAUSED via canSend. WAITING is locked, COMPLETED is read-only.
  const canConnect = order?.status === "LIVE" || order?.status === "PAUSED" || order?.status === "TESTING";
  const canSend = order?.status === "LIVE" || order?.status === "PAUSED";

  const mergeMessages = useCallback((incoming) => {
    if (!Array.isArray(incoming) || incoming.length === 0) return;
    setMessages((prev) => {
      const byId = new Map(prev.map((m) => [m.id, m]));
      let added = 0;
      for (const m of incoming) {
        if (!byId.has(m.id)) { byId.set(m.id, m); added += 1; }
      }
      if (added === 0) return prev;
      const merged = Array.from(byId.values());
      merged.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
      return merged;
    });
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await client.get(`/stations/${stationId}/messages`);
      mergeMessages(data.messages || []);
      setMeta({ order_number: data.order_number, customer_name: data.customer_name, order_id: data.order_id });
      // Count unread customer messages if drawer is closed.
      if (!expandedRef.current) {
        const custUnread = (data.messages || []).filter(
          (m) => m.sender_role === "customer" && !m.read_at,
        ).length;
        setUnread(custUnread);
      } else {
        setUnread(0);
      }
    } catch {}
  }, [stationId, mergeMessages]);

  useEffect(() => { loadHistory(); }, [loadHistory, order?.id, order?.status]);

  useEffect(() => {
    if (!canConnect) return;
    const jwt = localStorage.getItem("idea_token");
    if (!jwt) return;
    let closed = false;
    const clearPing = () => {
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
    };
    const connect = () => {
      const base = API.replace(/^http/, "ws").replace(/\/api$/, "");
      const ws = new WebSocket(`${base}/api/ws/chat/technician?token=${encodeURIComponent(jwt)}&station_id=${stationId}`);
      wsRef.current = ws;
      ws.onopen = () => {
        setConnected(true);
        backoffRef.current = 1000;
        // Reload history on every (re)connect to catch any gap.
        loadHistory();
        clearPing();
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === 1) {
            try { ws.send(JSON.stringify({ type: "ping" })); } catch {}
          }
        }, 20000);
      };
      ws.onmessage = (evt) => {
        try {
          const p = JSON.parse(evt.data);
          if (p.type === "message") {
            mergeMessages([p.message]);
            if (p.message.sender_role === "customer" && !expandedRef.current) {
              setUnread((u) => u + 1);
            }
          } else if (onSystemMessage && (p.type === "testing_state" || p.type === "testing_temp" || p.type === "hello")) {
            onSystemMessage(p);
          }
        } catch {}
      };
      ws.onclose = () => {
        setConnected(false);
        clearPing();
        if (!closed && canConnect) {
          setTimeout(connect, backoffRef.current);
          backoffRef.current = Math.min(backoffRef.current * 2, 15000);
        }
      };
      ws.onerror = () => ws.close();
    };
    connect();
    return () => {
      closed = true;
      clearPing();
      try { wsRef.current?.close(); } catch {}
    };
    // NOTE: `expanded` intentionally NOT in deps — drawer toggle must not reset
    // the socket. We read the current value via expandedRef inside handlers.
  }, [stationId, canConnect, loadHistory, mergeMessages]);

  useEffect(() => {
    if (expanded) {
      setUnread(0);
      try { scrollRef.current?.scrollTo?.({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); } catch {}
      // Mark server-side read via history load.
      client.get(`/stations/${stationId}/messages`).catch(() => {});
    }
  }, [messages, expanded, stationId]);

  const send = (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t || !canSend || !wsRef.current || wsRef.current.readyState !== 1) return;
    wsRef.current.send(JSON.stringify({ type: "message", text: t, temp_id: Date.now() }));
    setText("");
  };

  const sendVoice = useCallback(async (blob, duration, mimeType) => {
    const form = new FormData();
    const ext = (mimeType && mimeType.split("/")[1]?.split(";")[0]) || "webm";
    form.append("audio", blob, `voice-${Date.now()}.${ext}`);
    form.append("duration", String(duration));
    const { data } = await client.post(`/stations/${stationId}/voice`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    if (data?.message) mergeMessages([data.message]);
  }, [stationId, mergeMessages]);

  const authHeader = (() => {
    const jwt = typeof window !== "undefined" ? localStorage.getItem("idea_token") : null;
    return jwt ? `Bearer ${jwt}` : null;
  })();

  if (!order) return null;

  return (
    <div className="rounded-sm border" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid="tech-chat-panel">
      <button type="button" onClick={() => setExpanded((v) => !v)} className="w-full flex items-center justify-between px-6 py-4 text-left" data-testid="tech-chat-toggle">
        <div className="flex items-center gap-3">
          <MessageSquare size={16} style={{ color: "var(--accent)" }} />
          <div>
            <div className="text-white text-sm font-semibold tracking-tight">CUSTOMER CHAT</div>
            <div className="text-white/40 text-[11px]">
              {meta?.customer_name || order.customer_name || "—"} · #{meta?.order_number || order.order_number}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {unread > 0 && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: "var(--accent)", color: "#000" }} data-testid="tech-chat-unread">{unread}</span>
          )}
          {canConnect && !connected && <WifiOff size={12} className="text-white/40" />}
          <span className="text-white/40 text-xs">{expanded ? "▲" : "▼"}</span>
        </div>
      </button>
      {expanded && (
        <div className="px-6 pb-5">
          <div ref={scrollRef} className="max-h-64 overflow-y-auto pr-1 mb-3 border-t border-white/5 pt-3" data-testid="tech-chat-history">
            {messages.length === 0 ? (
              <div className="text-white/30 text-xs italic py-6 text-center">No messages yet</div>
            ) : messages.map((m) => (
              <div key={m.id} className={`flex mb-2 ${m.sender_role === "technician" ? "justify-end" : "justify-start"}`} data-testid={`tech-msg-${m.sender_role}`}>
                <div className="max-w-[80%] rounded-lg px-3 py-2 text-[13px]" style={{
                  background: m.sender_role === "technician" ? "rgba(0,229,255,0.14)" : "rgba(255,255,255,0.04)",
                  border: "1px solid " + (m.sender_role === "technician" ? "rgba(0,229,255,0.25)" : "rgba(255,255,255,0.08)"),
                  color: "#fff",
                }}>
                  {m.kind === "voice" ? (
                    <VoiceBubble
                      audioUrl={m.audio_url}
                      duration={m.duration_seconds}
                      contentType={m.audio_content_type}
                      authHeader={authHeader}
                      testid={`tech-voice-msg-${m.sender_role}`}
                    />
                  ) : (
                    <div className="whitespace-pre-wrap break-words">{m.text}</div>
                  )}
                  <div className="mt-1 text-[10px] text-white/40">{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                </div>
              </div>
            ))}
          </div>
          <form onSubmit={send} className="flex items-center gap-2 min-w-0">
            <input type="text" value={text} maxLength={500} disabled={!canSend} onChange={(e) => setText(e.target.value)} placeholder={canSend ? "Reply to customer…" : "Chat is available while the build is active"} className="flex-1 min-w-0 bg-black border border-white/20 h-10 rounded-sm px-3 text-sm text-white placeholder-white/30 focus:outline-none disabled:opacity-50" data-testid="tech-chat-input" />
            <VoiceRecorder disabled={!canSend} onSend={sendVoice} size={36} />
            <button type="submit" disabled={!canSend || !text.trim()} className="inline-flex items-center gap-2 h-10 px-4 rounded-sm font-semibold text-[13px] disabled:opacity-40" style={{ background: "var(--accent)", color: "#000" }} data-testid="tech-chat-send"><Send size={14} /> SEND</button>
          </form>
        </div>
      )}
    </div>
  );
}
